import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import { useMigrationCheckout } from '../../hooks/useBilling';
import type { BillingStatus } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  billing: BillingStatus | undefined;
}

export default function MigrateModal({ isOpen, onClose, billing }: Props) {
  const migrationCheckout = useMigrationCheckout();

  const providerName =
    billing?.provider === 'foxy' ? 'FoxyCart' : 'Billsby';

  const handleMigrate = () => {
    migrationCheckout.mutate();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Migrate Your Billing">
      <div className="space-y-4">
        <p className="text-gray-600">
          You're about to migrate your subscription from {providerName} to our
          new billing system powered by Stripe.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            What to expect:
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li className="flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>Your subscription will continue without interruption</span>
            </li>
            <li className="flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>You'll enter new payment details in Stripe Checkout</span>
            </li>
            <li className="flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>Your old {providerName} subscription will be cancelled</span>
            </li>
          </ul>
        </div>

        {migrationCheckout.isError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">
              {migrationCheckout.error instanceof Error
                ? migrationCheckout.error.message
                : 'Failed to start migration. Please try again or contact support.'}
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            disabled={migrationCheckout.isPending}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleMigrate}
            disabled={migrationCheckout.isPending}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {migrationCheckout.isPending ? (
              <>
                <Spinner
                  size="sm"
                  className="border-white border-t-transparent"
                />
                <span>Processing...</span>
              </>
            ) : (
              'Continue to Stripe'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
